import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_RECORDING_LIST_FILTER } from '../historyUtils';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { parseDateParts } from '../scheduleUtils';
import { WheelColumn } from './ScheduleWheelPicker';
const YEAR_SPAN_BACK = 8;
/**
 * 补零。
 *
 * @param {number} value - 数值。
 * @returns {string}
 */
function pad2(value) {
    return String(value).padStart(2, '0');
}
/**
 * 拼 yyyy-MM-dd。
 *
 * @param {number} year - 年。
 * @param {number} month - 月。
 * @param {number} day - 日。
 * @returns {string}
 */
function toIsoDate(year, month, day) {
    const maxDay = new Date(year, month, 0).getDate();
    const safeDay = Math.min(day, maxDay);
    return `${year}-${pad2(month)}-${pad2(safeDay)}`;
}
/**
 * 展示为 2026年7月14日。
 *
 * @param {string} isoDate - yyyy-MM-dd。
 * @returns {string}
 */
function toZhDate(isoDate) {
    if (!isoDate) {
        return '';
    }
    const parts = parseDateParts(isoDate);
    return `${parts.year}年${parts.month}月${parts.day}日`;
}
/**
 * 录制筛选：桌面锚在漏斗下，手机顶部抽屉；日期为中文文案 + 年/月/日滚轮。
 *
 * @param {IProps} props - 属性。
 * @returns {JSX.Element}
 */
export default function RecordingFilterPanel({ anchorRef, draftFilter, isMobile, onClose, onConfirm, onDraftChange }) {
    const [openField, setOpenField] = useState(null);
    const [popoverPos, setPopoverPos] = useState({ right: 12, top: 48 });
    const now = new Date();
    const yearOptions = useMemo(() => {
        const current = now.getFullYear();
        const start = current - YEAR_SPAN_BACK;
        return Array.from({ length: current - start + 1 }, (_, index) => {
            const year = start + index;
            return {
                label: `${year}年`,
                value: String(year)
            };
        });
    }, [now.getFullYear()]);
    const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, index) => ({
        label: `${index + 1}月`,
        value: String(index + 1)
    })), []);
    const wheelIso = openField === 'end'
        ? (draftFilter.endDate || draftFilter.startDate || now.toISOString().slice(0, 10))
        : (draftFilter.startDate || now.toISOString().slice(0, 10));
    const wheelParts = parseDateParts(wheelIso);
    const dayOptions = useMemo(() => {
        const maxDay = new Date(wheelParts.year, wheelParts.month, 0).getDate();
        return Array.from({ length: maxDay }, (_, index) => ({
            label: `${index + 1}日`,
            value: String(index + 1)
        }));
    }, [wheelParts.month, wheelParts.year]);
    useLayoutEffect(() => {
        const updatePos = () => {
            const el = anchorRef.current;
            if (!el) {
                return;
            }
            const rect = el.getBoundingClientRect();
            setPopoverPos({
                right: Math.max(8, window.innerWidth - rect.right),
                top: rect.bottom + 6
            });
        };
        updatePos();
        window.addEventListener('resize', updatePos);
        window.addEventListener('scroll', updatePos, true);
        return () => {
            window.removeEventListener('resize', updatePos);
            window.removeEventListener('scroll', updatePos, true);
        };
    }, [anchorRef]);
    useEffect(() => {
        logger.info(`[录制] ${new Date().toISOString()} 打开筛选面板 | mobile=${isMobile}`);
    }, [isMobile]);
    /**
     * 打开某一侧日期滚轮。
     *
     * @param {DateFieldKey} field - 起始或终止。
     * @returns {void}
     */
    const openWheel = (field) => {
        setOpenField(prev => (prev === field ? null : field));
        logger.info(`[录制] ${new Date().toISOString()} 切换筛选日期滚轮 | field=${field}`);
    };
    /**
     * 滚轮改写当前字段日期。
     *
     * @param {Partial<{day: number; month: number; year: number;}>} patch - 变更。
     * @returns {void}
     */
    const applyWheel = (patch) => {
        if (!openField) {
            return;
        }
        const nextIso = toIsoDate(patch.year ?? wheelParts.year, patch.month ?? wheelParts.month, patch.day ?? wheelParts.day);
        const next = {
            ...draftFilter,
            time: 'custom',
            [openField === 'end' ? 'endDate' : 'startDate']: nextIso
        };
        if (next.startDate && next.endDate && next.startDate > next.endDate) {
            if (openField === 'start') {
                next.endDate = nextIso;
            }
            else {
                next.startDate = nextIso;
            }
        }
        onDraftChange(next);
        logger.debug(`[录制] ${new Date().toISOString()} 滚轮改日期 | field=${openField} date=${nextIso}`);
    };
    /**
     * 渲染日期槽。
     *
     * @param {DateFieldKey} field - 起始或终止。
     * @param {string} value - yyyy-MM-dd。
     * @param {string} emptyLabel - 空值文案。
     * @returns {JSX.Element}
     */
    const renderDateField = (field, value, emptyLabel) => (React.createElement("button", { "aria-label": emptyLabel, className: `portal-rec-date-field${openField === field ? ' is-open' : ''}${value ? ' has-value' : ''}`, onClick: e => {
            e.stopPropagation();
            openWheel(field);
        }, type: 'button' },
        React.createElement("span", null, value ? toZhDate(value) : emptyLabel)));
    const sheetStyle = isMobile
        ? undefined
        : {
            right: popoverPos.right,
            top: popoverPos.top
        };
    const node = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `portal-rec-filter-mask${isMobile ? ' is-drawer' : ' is-popover'}`, onClick: () => {
                logger.info(`[录制] ${new Date().toISOString()} 关闭筛选面板`);
                onClose();
            }, role: 'presentation' }),
        React.createElement("div", { className: `portal-rec-filter-sheet${isMobile ? ' is-drawer' : ' is-popover'}`, onClick: e => e.stopPropagation(), style: sheetStyle },
            React.createElement("div", { className: 'portal-rec-filter-label' }, M.RECORDINGS_FILTER_SCENE),
            React.createElement("div", { className: 'portal-rec-chip-row' },
                React.createElement("button", { className: draftFilter.scene === 'all' ? 'is-active' : undefined, onClick: () => onDraftChange({ ...draftFilter, scene: 'all' }), type: 'button' }, M.RECORDINGS_FILTER_ALL),
                React.createElement("button", { className: draftFilter.scene === 'in-meeting' ? 'is-active' : undefined, onClick: () => onDraftChange({ ...draftFilter, scene: 'in-meeting' }), type: 'button' }, M.RECORDINGS_FILTER_IN_MEETING),
                React.createElement("button", { className: draftFilter.scene === 'out-meeting' ? 'is-active' : undefined, onClick: () => onDraftChange({ ...draftFilter, scene: 'out-meeting' }), type: 'button' }, M.RECORDINGS_FILTER_OUT_MEETING)),
            React.createElement("div", { className: 'portal-rec-filter-label' }, M.RECORDINGS_FILTER_TIME),
            React.createElement("div", { className: 'portal-rec-chip-row' },
                React.createElement("button", { className: draftFilter.time === 'all' ? 'is-active' : undefined, onClick: () => {
                        setOpenField(null);
                        onDraftChange({
                            ...draftFilter,
                            time: 'all',
                            startDate: '',
                            endDate: ''
                        });
                    }, type: 'button' }, M.RECORDINGS_FILTER_ALL),
                React.createElement("button", { className: draftFilter.time === '1m' ? 'is-active' : undefined, onClick: () => {
                        setOpenField(null);
                        onDraftChange({
                            ...draftFilter,
                            time: '1m',
                            startDate: '',
                            endDate: ''
                        });
                    }, type: 'button' }, M.RECORDINGS_FILTER_LAST_MONTH),
                React.createElement("button", { className: draftFilter.time === '3m' ? 'is-active' : undefined, onClick: () => {
                        setOpenField(null);
                        onDraftChange({
                            ...draftFilter,
                            time: '3m',
                            startDate: '',
                            endDate: ''
                        });
                    }, type: 'button' }, M.RECORDINGS_FILTER_LAST_3_MONTHS)),
            React.createElement("div", { className: 'portal-rec-date-row' },
                renderDateField('start', draftFilter.startDate, M.RECORDINGS_FILTER_START),
                React.createElement("span", null, "\u2014"),
                renderDateField('end', draftFilter.endDate, M.RECORDINGS_FILTER_END)),
            openField ? (React.createElement("div", { className: 'portal-rec-wheel' },
                React.createElement("div", { className: 'portal-rec-wheel-highlight' }),
                React.createElement(WheelColumn, { onChange: value => applyWheel({ year: Number(value) }), options: yearOptions, value: String(wheelParts.year) }),
                React.createElement(WheelColumn, { onChange: value => applyWheel({ month: Number(value) }), options: monthOptions, value: String(wheelParts.month) }),
                React.createElement(WheelColumn, { onChange: value => applyWheel({ day: Number(value) }), options: dayOptions, value: String(wheelParts.day) }))) : null,
            React.createElement("div", { className: 'portal-rec-filter-actions' },
                React.createElement("button", { className: 'portal-rec-filter-reset', onClick: () => {
                        setOpenField(null);
                        onDraftChange({ ...DEFAULT_RECORDING_LIST_FILTER });
                        logger.info(`[录制] ${new Date().toISOString()} 重置筛选草稿`);
                    }, type: 'button' }, M.RECORDINGS_FILTER_RESET),
                React.createElement("button", { className: 'portal-rec-filter-confirm', onClick: () => onConfirm(draftFilter), type: 'button' }, M.RECORDINGS_FILTER_CONFIRM)))));
    if (typeof document === 'undefined') {
        return node;
    }
    return createPortal(node, document.body);
}
